import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { trackPageView } from "@/utils/analytics";
import { Toaster } from "@/components/ui/sonner";

// Pages
import { LandingPage } from "@/pages/Landing";
import { LoginPage } from "@/pages/Login";
import { RegisterPage } from "@/pages/Register";
import { ClientDashboard } from "@/pages/ClientDashboard";
import { OnboardingPage } from "@/pages/Onboarding";
import { WeeklyLogPage } from "@/pages/WeeklyLog";
import { ConsultantDashboard } from "@/pages/ConsultantDashboard";
import { ClientDetailPage } from "@/pages/ClientDetail";
import { PaymentSuccessPage } from "@/pages/PaymentSuccess";
import { IdealClientPage } from "@/pages/IdealClient";
import { VisionGoalsPage } from "@/pages/VisionGoals";
import { ActionPlansPage } from "@/pages/ActionPlans";
import { SalesTrackingPage } from "@/pages/SalesTracking";
import { BrandGuidelinesPage } from "@/pages/BrandGuidelines";
import { BusinessPlanPage } from "@/pages/BusinessPlan";
import { RapidDashboard } from "@/pages/RapidDashboard";
import { RapidIntakePage } from "@/pages/RapidIntake";
import { RapidSession1Page } from "@/pages/RapidSession1";
import { RapidSession2Page } from "@/pages/RapidSession2";
import { RapidSession3Page } from "@/pages/RapidSession3";
import { RapidMomentumPage } from "@/pages/RapidMomentum";
import { RapidCompletionPage } from "@/pages/RapidCompletion";
import { ConsultantRapidDetailPage } from "@/pages/ConsultantRapidDetail";
import { ConsultantProjectsPage } from "@/pages/ConsultantProjects";
import PartnershipInquiry from "@/pages/PartnershipInquiry";
import { ClientProjectsPage } from "@/pages/ClientProjects";
import { ProjectIntakePage } from "@/pages/ProjectIntake";
import { ProjectScopeRequest } from "@/pages/ProjectScopeRequest";
import { PackageDetail } from "@/pages/PackageDetail";
import { AdvancedAnalyticsDashboard } from "@/pages/AdvancedAnalyticsDashboard";
import { MyOverviewPage } from "@/pages/MyOverview";
import { AcceptInvitePage } from "@/pages/AcceptInvite";
import { AcceptNDAPage } from "@/pages/AcceptNDA";
import { TimeBudgetPage } from "@/pages/TimeBudget";
import { TimeBudgetActualsPage } from "@/pages/TimeBudgetActuals";
import { SOPGeneratorPage } from "@/pages/SOPGenerator";
import { PaymentTrackerPage } from "@/pages/PaymentTracker";
import { ConsultantTimeBudgetPage } from "@/pages/ConsultantTimeBudget";
import { StoryBrandPage } from "@/pages/StoryBrand";
import { CustomReportPage } from "@/pages/CustomReport";
import { HelpPage } from "@/pages/HelpPage";
import { CommunityDashboard } from "@/pages/CommunityDashboard";
import CommunityPage from "@/pages/CommunityPage";
import { ForgotPasswordPage } from "@/pages/ForgotPassword";
import { ResetPasswordPage } from "@/pages/ResetPassword";
import { ScalingDashboard } from "@/components/ScalingDashboard";
import { ClientIntakePage } from "@/pages/ClientIntake";
import { ReferralsPage } from "@/pages/ReferralsPage";
import { PodcastDashboard } from "@/pages/PodcastDashboard";
import { PodcastIntakePage } from "@/pages/PodcastIntake";
import { PodcastWeekPage } from "@/pages/PodcastWeek";
import PodcastManagement from "@/pages/PodcastManagement";
import { PricingPage } from "@/pages/PricingPage";
import { EngagementsPage } from "@/pages/EngagementsPage";
import { ScaleApplication } from "@/pages/ScaleApplication";
import { SubscriptionPage } from "@/pages/SubscriptionPage";
import { LeaderboardPage } from "@/pages/LeaderboardPage";
import { PackagesPage } from "@/pages/PackagesPage";
import { PublicEventPage } from "@/pages/PublicEvent";
import { PodcastAcademyAdmin } from "@/pages/PodcastAcademyAdmin";
import { ConsultantOnboarding } from "@/pages/ConsultantOnboarding";
import ConsultantBlog from "@/pages/ConsultantBlog";
import { ClarityAssessment } from "@/pages/ClarityAssessment";
import SecurityPrivacy from "@/pages/SecurityPrivacy";
import SponsorshipPage from "@/pages/SponsorshipPage";
import SponsorshipDashboard from "@/pages/SponsorshipDashboard";
import SponsorshipSuccess from "@/pages/SponsorshipSuccess";
import TrackPacket from "@/pages/TrackPacket";
import SponsorPortal from "@/pages/SponsorPortal";
import SponsorDashboardPage from "@/pages/SponsorDashboardPage";
import PitchDeck from "@/pages/PitchDeck";
import { BlogPage, BlogPostPage } from "@/pages/BlogPage";
import { ChatWidget } from "@/components/ChatWidget";
import MilesChatWidget from "@/components/MilesChatWidget";
import RecentActivity from "@/components/RecentActivity";
import StickyMailboxButton from "@/components/StickyMailboxButton";
import { FAQPage } from "@/pages/FAQPage";
import { CapabilityStatement } from "@/pages/CapabilityStatement";
import { PodcastMediaPage } from "@/pages/PodcastMediaPage";
import { CaseNarrativesPage } from "@/pages/CaseNarrativesPage";
import { JamilahProfile } from "@/pages/JamilahProfile";
import { PressPage } from "@/pages/PressPage";
import { LocationPage } from "@/pages/LocationPage";
import BusinessFormation from "@/pages/BusinessFormation";
import ContractorConsulting from "@/pages/ContractorConsulting";
import EntrepreneurConsulting from "@/pages/EntrepreneurConsulting";
import InfluencerConsulting from "@/pages/InfluencerConsulting";
import AthleteConsulting from "@/pages/AthleteConsulting";
import EntertainmentConsulting from "@/pages/EntertainmentConsulting";
import PhiladelphiaConsulting from "@/pages/PhiladelphiaConsulting";
import BusinessConsultingPhiladelphia from "@/pages/BusinessConsultingPhiladelphia";
import AIConsultingPhiladelphia from "@/pages/AIConsultingPhiladelphia";
import AIAutomation from "@/pages/AIAutomation";
import BusinessSystems from "@/pages/BusinessSystems";
import OperationsConsulting from "@/pages/OperationsConsulting";
import BusinessStrategy from "@/pages/BusinessStrategy";
import DigitalTransformation from "@/pages/DigitalTransformation";
import MarketingGrowth from "@/pages/MarketingGrowth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
import { createContext, useContext } from "react";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Helper to get stored token (checks localStorage first, then sessionStorage for private browsing)
const getStoredToken = () => {
  try {
    const token = localStorage.getItem("auth_token");
    if (token) return token;
  } catch (_e) {
    console.warn("localStorage not available:", _e);
  }
  
  // Fallback to sessionStorage (for private browsing mode)
  try {
    return sessionStorage.getItem("auth_token");
  } catch (_e) {
    console.warn("sessionStorage not available:", _e);
    return null;
  }
};

const setStoredToken = (token) => {
  try {
    if (token) {
      localStorage.setItem("auth_token", token);
      // Verify the write was successful
      const stored = localStorage.getItem("auth_token");
      if (stored !== token) {
        console.error("Token storage verification failed");
      }
    } else {
      localStorage.removeItem("auth_token");
    }
  } catch (_e) {
    console.error("Error writing token to localStorage:", _e);
  }
};

// Custom fetch with auth header - uses Authorization header instead of cookies
// This avoids CORS credential issues with iOS Safari and CDN proxies
export const authFetch = async (url, options = {}) => {
  const token = getStoredToken();
  const headers = { ...options.headers };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  // Auto-add Content-Type for JSON bodies
  if (options.body && typeof options.body === 'string' && !headers["Content-Type"]) {
    try {
      JSON.parse(options.body);
      headers["Content-Type"] = "application/json";
    } catch {
      // Not JSON, don't add header
    }
  }
  
  return fetch(url, {
    ...options,
    headers,
    mode: "cors"
  });
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authToken, setAuthToken] = useState(() => getStoredToken());

  const checkAuth = async (skipIfHasUser = false) => {
    // Skip check if we already have user data (just logged in)
    if (skipIfHasUser && user) {
      setLoading(false);
      return;
    }
    
    try {
      const token = authToken || getStoredToken();
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${API}/auth/me`, {
        mode: "cors",
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        setStoredToken(null);
        setAuthToken(null);
      }
    } catch (_error) {
      console.error("Auth check error:", _error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = (userData) => {
    // Store token first, then set user state
    if (userData.token) {
      setStoredToken(userData.token);
      setAuthToken(userData.token);
    }
    setUser(userData);
  };

  const logout = async () => {
    try {
      const token = authToken || getStoredToken();
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        mode: "cors",
        headers: token ? { "Authorization": `Bearer ${token}` } : {}
      });
    } catch (_error) {
      console.error("Logout error:", _error);
    }
    setUser(null);
    setStoredToken(null);
    setAuthToken(null);
  };

  // Provide authFetch as part of context for consistent auth headers
  const authenticatedFetch = async (url, options = {}) => {
    const token = authToken || getStoredToken();
    const headers = { ...options.headers };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    return fetch(url, {
      ...options,
      headers,
      mode: "cors"
    });
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, authenticatedFetch }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredRole && user.role !== requiredRole) {
    // Redirect to appropriate dashboard based on role
    if (user.role === "consultant") {
      return <Navigate to="/consultant" replace />;
    } else if (user.role === "community_member" || user.role === "community") {
      return <Navigate to="/community" replace />;
    } else if (user.role === "sponsor") {
      return <Navigate to="/sponsor-dashboard" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

// Auth Callback for Google OAuth
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await fetch(`${API}/auth/session`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            
            body: JSON.stringify({ session_id: sessionId })
          });
          
          if (response.ok) {
            const userData = await response.json();
            
            // Store token first to ensure it's available for subsequent requests
            if (userData.token) {
              try {
                localStorage.setItem("auth_token", userData.token);
              } catch (_e) {
                console.error("Failed to store token:", _e);
              }
            }
            
            login(userData);
            
            // Small delay to ensure state is synced before navigation
            await new Promise(resolve => setTimeout(resolve, 100));
            
            if (!userData.onboarded && userData.role === "client") {
              navigate("/onboarding", { replace: true, state: { user: userData } });
            } else if (userData.role === "consultant") {
              navigate("/consultant", { replace: true, state: { user: userData } });
            } else if (userData.role === "community_member" || userData.role === "community") {
              navigate("/community", { replace: true, state: { user: userData } });
            } else {
              navigate("/dashboard", { replace: true, state: { user: userData } });
            }
          } else {
            console.error("Session processing failed:", response.status);
            navigate("/login", { replace: true });
          }
        } catch (_error) {
          console.error("Session processing _error:", _error);
          navigate("/login", { replace: true });
        }
      } else {
        navigate("/login", { replace: true });
      }
    };

    processSession();
  }, [location, navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-neutral-600">Signing you in...</p>
      </div>
    </div>
  );
};

// Scaling Dashboard Wrapper Component
const ScalingDashboardWrapper = () => {
  const { user, logout } = useAuth();
  return <ScalingDashboard user={user} logout={logout} />;
};

// Chat Widget Wrapper - shows for authenticated users only
const ChatWidgetWrapper = () => {
  const { user } = useAuth();
  const location = useLocation();
  
  // Don't show on public pages
  const publicPaths = ["/", "/login", "/register", "/forgot-password", "/reset-password", "/accept-invite", "/accept-nda", "/pricing", "/packages", "/security", "/clarity-assessment", "/assessment", "/partnership", "/sponsors", "/sponsorship", "/sponsorship/success", "/sponsorship/track"];
  if (!user || publicPaths.includes(location.pathname) || location.pathname.startsWith("/partner/") || location.pathname === "/pitch-deck" || location.pathname.startsWith("/blog")) {
    return null;
  }
  
  return <ChatWidget user={user} />;
};

function AppRouter() {
  const location = useLocation();
  
  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/security" element={<SecurityPrivacy />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/clarity-assessment" element={<ClarityAssessment />} />
      <Route path="/assessment" element={<ClarityAssessment />} />
      <Route path="/partnership" element={<PartnershipInquiry />} />
      <Route path="/sponsors" element={<PartnershipInquiry />} />
      <Route path="/partner/:slug" element={<SponsorPortal />} />
      <Route path="/pitch-deck" element={<PitchDeck />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/capability-statement" element={<CapabilityStatement />} />
      <Route path="/media" element={<PodcastMediaPage />} />
      <Route path="/case-narratives" element={<CaseNarrativesPage />} />
      <Route path="/jamilah-n-lawry" element={<JamilahProfile />} />
      <Route path="/press" element={<PressPage />} />
      <Route path="/location" element={<LocationPage />} />
      <Route path="/business-formation" element={<BusinessFormation />} />
      <Route path="/contractor-business-consulting" element={<ContractorConsulting />} />
      <Route path="/entrepreneur-consulting" element={<EntrepreneurConsulting />} />
      <Route path="/influencer-business-consulting" element={<InfluencerConsulting />} />
      <Route path="/athlete-business-consulting" element={<AthleteConsulting />} />
      <Route path="/entertainment-business-consulting" element={<EntertainmentConsulting />} />
      <Route path="/philadelphia-business-consulting" element={<PhiladelphiaConsulting />} />
      <Route path="/business-consulting-philadelphia" element={<BusinessConsultingPhiladelphia />} />
      <Route path="/ai-consulting-philadelphia" element={<AIConsultingPhiladelphia />} />
      <Route path="/ai-automation" element={<AIAutomation />} />
      <Route path="/business-systems" element={<BusinessSystems />} />
      <Route path="/operations-consulting" element={<OperationsConsulting />} />
      <Route path="/business-strategy" element={<BusinessStrategy />} />
      <Route path="/digital-transformation" element={<DigitalTransformation />} />
      <Route path="/marketing-growth" element={<MarketingGrowth />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/accept-nda" element={<AcceptNDAPage />} />
      <Route path="/sponsorship" element={<SponsorshipPage />} />
      <Route path="/sponsorship/success" element={<SponsorshipSuccess />} />
      <Route path="/sponsorship/track" element={<TrackPacket />} />
      <Route
        path="/sponsorship-dashboard"
        element={
          <ProtectedRoute>
            <SponsorshipDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sponsor-dashboard"
        element={
          <ProtectedRoute>
            <SponsorDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/intake"
        element={
          <ProtectedRoute>
            <ClientIntakePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/community"
        element={
          <ProtectedRoute>
            <CommunityPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/weekly-log"
        element={
          <ProtectedRoute>
            <WeeklyLogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ideal-client"
        element={
          <ProtectedRoute>
            <IdealClientPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vision-goals"
        element={
          <ProtectedRoute>
            <VisionGoalsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/action-plans"
        element={
          <ProtectedRoute>
            <ActionPlansPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales"
        element={
          <ProtectedRoute>
            <SalesTrackingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/brand-guidelines"
        element={
          <ProtectedRoute>
            <BrandGuidelinesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/business-plan"
        element={
          <ProtectedRoute>
            <BusinessPlanPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consultant"
        element={
          <ProtectedRoute requiredRole="consultant">
            <ConsultantDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consultant/client/:clientId"
        element={
          <ProtectedRoute requiredRole="consultant">
            <ClientDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consultant/projects"
        element={
          <ProtectedRoute requiredRole="consultant">
            <ConsultantProjectsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consultant/analytics"
        element={
          <ProtectedRoute requiredRole="consultant">
            <AdvancedAnalyticsDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <ClientProjectsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/project-intake"
        element={
          <ProtectedRoute>
            <ProjectIntakePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-overview"
        element={
          <ProtectedRoute>
            <MyOverviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/time-budget"
        element={
          <ProtectedRoute>
            <TimeBudgetPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/time-budget/actuals"
        element={
          <ProtectedRoute>
            <TimeBudgetActualsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sop-generator"
        element={
          <ProtectedRoute>
            <SOPGeneratorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/storybrand"
        element={
          <ProtectedRoute>
            <StoryBrandPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/custom-report"
        element={
          <ProtectedRoute>
            <CustomReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consultant/payments"
        element={
          <ProtectedRoute requiredRole="consultant">
            <PaymentTrackerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consultant/time-budget"
        element={
          <ProtectedRoute requiredRole="consultant">
            <ConsultantTimeBudgetPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <HelpPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/referrals"
        element={
          <ProtectedRoute>
            <ReferralsPage />
          </ProtectedRoute>
        }
      />
      {/* R.A.P.I.D.™ Framework Routes */}
      <Route
        path="/rapid"
        element={
          <ProtectedRoute>
            <RapidDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rapid/intake"
        element={
          <ProtectedRoute>
            <RapidIntakePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rapid/session/1"
        element={
          <ProtectedRoute>
            <RapidSession1Page />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rapid/session/2"
        element={
          <ProtectedRoute>
            <RapidSession2Page />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rapid/session/3"
        element={
          <ProtectedRoute>
            <RapidSession3Page />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rapid/momentum"
        element={
          <ProtectedRoute>
            <RapidMomentumPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rapid/completion"
        element={
          <ProtectedRoute>
            <RapidCompletionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consultant/rapid/:clientId"
        element={
          <ProtectedRoute requiredRole="consultant">
            <ConsultantRapidDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consultant/podcast-academy"
        element={
          <ProtectedRoute requiredRole="consultant">
            <PodcastAcademyAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consultant/onboarding"
        element={
          <ProtectedRoute requiredRole="consultant">
            <ConsultantOnboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consultant/blog"
        element={
          <ProtectedRoute requiredRole="consultant">
            <ConsultantBlog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/scaling"
        element={
          <ProtectedRoute>
            <ScalingDashboardWrapper />
          </ProtectedRoute>
        }
      />
      {/* Podcast Launch Academy Routes */}
      <Route
        path="/podcast"
        element={
          <ProtectedRoute>
            <PodcastDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/podcast/intake"
        element={
          <ProtectedRoute>
            <PodcastIntakePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/podcast/week/:week"
        element={
          <ProtectedRoute>
            <PodcastWeekPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/podcast/manage"
        element={
          <ProtectedRoute>
            <PodcastManagement />
          </ProtectedRoute>
        }
      />
      {/* Pricing & Payments */}
      <Route path="/packages" element={<PackagesPage />} />
      <Route path="/event/:eventId" element={<PublicEventPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/engagements" element={<EngagementsPage />} />
      <Route path="/apply/scale" element={<ScaleApplication />} />
      <Route path="/request-project" element={<ProjectScopeRequest />} />
      <Route path="/pricing/:packageId" element={<PackageDetail />} />
      <Route
        path="/my-pricing"
        element={
          <ProtectedRoute>
            <PricingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leaderboard"
        element={
          <ProtectedRoute>
            <LeaderboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute>
            <PaymentSuccessPage />
          </ProtectedRoute>
        }
      />
    </Routes>
    <ChatWidgetWrapper />
    <MilesChatWidgetWrapper />
    <RecentActivityWrapper />
    <StickyMailboxButton />
    </>
  );
}

// Recent Activity — shows only on public pages
const RecentActivityWrapper = () => {
  const { user } = useAuth();
  const location = useLocation();
  const publicPages = ["/", "/pricing", "/faq", "/clarity-assessment", "/business-formation", "/capability-statement", "/case-narratives", "/media", "/press", "/location", "/jamilah-n-lawry"];
  const isPublic = publicPages.includes(location.pathname) || location.pathname.startsWith("/contractor") || location.pathname.startsWith("/entrepreneur") || location.pathname.startsWith("/influencer") || location.pathname.startsWith("/athlete") || location.pathname.startsWith("/entertainment") || location.pathname.startsWith("/philadelphia") || location.pathname.startsWith("/business-consulting") || location.pathname.startsWith("/ai-") || location.pathname.startsWith("/operations") || location.pathname.startsWith("/business-strategy") || location.pathname.startsWith("/digital-") || location.pathname.startsWith("/marketing") || location.pathname.startsWith("/business-systems") || location.pathname.startsWith("/blog");
  if (!isPublic || user) return null;
  return <RecentActivity />;
};

// Miles Chat Widget - shows for ALL visitors (public)
const MilesChatWidgetWrapper = () => {
  const { user } = useAuth();
  const location = useLocation();
  
  // Don't show Miles on authenticated pages where ChatWidget is active
  // to avoid two floating buttons
  const publicPaths = ["/", "/login", "/register", "/forgot-password", "/reset-password", "/accept-invite", "/accept-nda", "/pricing", "/packages", "/security", "/clarity-assessment", "/assessment", "/partnership", "/sponsors", "/sponsorship", "/sponsorship/success", "/sponsorship/track", "/faq", "/capability-statement", "/media", "/case-narratives", "/pitch-deck", "/jamilah-n-lawry", "/press", "/location", "/business-formation", "/contractor-business-consulting", "/entrepreneur-consulting", "/influencer-business-consulting", "/athlete-business-consulting", "/entertainment-business-consulting", "/philadelphia-business-consulting", "/business-consulting-philadelphia", "/ai-consulting-philadelphia", "/ai-automation", "/business-systems", "/operations-consulting", "/business-strategy", "/digital-transformation", "/marketing-growth"];
  const isPublicPage = publicPaths.includes(location.pathname) || location.pathname.startsWith("/partner/") || location.pathname.startsWith("/blog");
  
  // Show Miles on public pages (no ChatWidget there) OR when no user is logged in
  if (!isPublicPage && user) return null;
  
  return <MilesChatWidget />;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
